<script setup lang="ts">
import { computed, ref } from 'vue'
import { collator, t } from '../core/i18n'
import { formatCoordinate } from '../core/coordinates'
import { navigate } from '../core/router'
import { session } from '../core/session'
import type { VillageGroup, VillageSummary, WorldSummary } from '../core/types'
import { villagePreferenceKey } from '../core/villages'

const props = defineProps<{
  world: WorldSummary
  villageId?: string | undefined
}>()
const query = ref('')
const groupId = ref('')
const page = ref(0)
const pageSize = 50

const groups = computed<VillageGroup[]>(() => props.world.groups)
const sorted = computed<VillageSummary[]>(() => {
  const group = groups.value.find((item) => item.groupId === groupId.value)
  const allowed = group ? new Set(group.villageIds) : null
  const q = query.value.trim().toLocaleLowerCase()
  return props.world.villages
    .filter((village) => allowed === null || allowed.has(village.villageId))
    .filter((village) => {
      const rawCoordinate = `${String(village.coordinate.x)}|${String(village.coordinate.y)}`
      const displayCoordinate = formatCoordinate(
        village.coordinate.x,
        village.coordinate.y,
      )
      return (
        q === '' ||
        village.displayName.toLocaleLowerCase().includes(q) ||
        rawCoordinate.includes(q) ||
        displayCoordinate.includes(q)
      )
    })
    .slice()
    .sort((a, b) => collator().compare(a.displayName, b.displayName))
})
const pages = computed(() =>
  Math.max(1, Math.ceil(sorted.value.length / pageSize)),
)
const visible = computed(() =>
  sorted.value.slice(page.value * pageSize, page.value * pageSize + pageSize),
)
const selected = computed(
  () =>
    props.world.villages.find(
      (village) => village.villageId === props.villageId,
    ) ?? null,
)

function go(village: VillageSummary): void {
  localStorage.setItem(
    villagePreferenceKey(
      session.account?.accountId ?? 'anonymous',
      props.world.worldId,
    ),
    village.villageId,
  )
  navigate(
    `/world/${props.world.worldId}/village/${village.villageId}/dashboard`,
  )
}
function move(delta: number): void {
  const index = sorted.value.findIndex(
    (village) => village.villageId === props.villageId,
  )
  if (index < 0 || sorted.value.length === 0) return
  const next =
    sorted.value[(index + delta + sorted.value.length) % sorted.value.length]
  if (next) go(next)
}
function resetPage(): void {
  page.value = 0
}
</script>

<template>
  <div class="village-switcher">
    <div class="village-switcher-row">
      <button
        class="icon-button"
        type="button"
        :aria-label="t('common.previous')"
        @click="move(-1)"
      >
        ‹
      </button>
      <details class="switcher-details">
        <summary>
          <strong>{{ selected?.displayName ?? t('village.switch') }}</strong>
          <span v-if="selected" class="muted"
            >{{
              formatCoordinate(selected.coordinate.x, selected.coordinate.y)
            }}</span
          >
        </summary>
        <div class="switcher-panel">
          <label>
            <span>{{ t('village.search') }}</span>
            <input v-model="query" type="search" @input="resetPage" />
          </label>
          <label v-if="groups.length > 0">
            <span>{{ t('village.group') }}</span>
            <select v-model="groupId" @change="resetPage">
              <option value="">{{ t('common.all') }}</option>
              <option
                v-for="group in groups"
                :key="group.groupId"
                :value="group.groupId"
              >
                {{ group.name }}
              </option>
            </select>
          </label>
          <div v-if="visible.length === 0" class="empty-inline">
            {{ t('village.noMatch') }}
          </div>
          <button
            v-for="village in visible"
            :key="village.villageId"
            class="switcher-option"
            type="button"
            @click="go(village)"
          >
            <span>{{ village.displayName }}</span
            ><span class="mono"
              >{{
                formatCoordinate(village.coordinate.x, village.coordinate.y)
              }}</span
            >
          </button>
          <div v-if="pages > 1" class="pagination">
            <button type="button" :disabled="page === 0" @click="page--">
              {{ t('common.previous') }}
            </button>
            <span>{{ page + 1 }} / {{ pages }}</span>
            <button type="button" :disabled="page >= pages - 1" @click="page++">
              {{ t('common.next') }}
            </button>
          </div>
        </div>
      </details>
      <button
        class="icon-button"
        type="button"
        :aria-label="t('common.next')"
        @click="move(1)"
      >
        ›
      </button>
    </div>
  </div>
</template>
